import i18n from "@/i18n";

/**
 * User-friendly error messages for speech-to-text, in the interface language.
 *
 * Centralizes the mapping of browser errors (getUserMedia) and the voice-server
 * protocol (`error` events + WebSocket close codes, cf. the voice-server repo
 * README) to displayable text, so STT hooks never surface a raw technical
 * message to the user.
 */

/** Microphone access / local audio setup errors (getUserMedia, AudioContext). */
export function describeMicrophoneError(err: unknown): string {
  if (err instanceof DOMException) {
    switch (err.name) {
      case "NotAllowedError":
      case "PermissionDeniedError":
      case "SecurityError":
        return i18n.t("core:speech.microphone.denied");
      case "NotFoundError":
      case "DevicesNotFoundError":
        return i18n.t("core:speech.microphone.notFound");
      case "NotReadableError":
      case "TrackStartError":
        return i18n.t("core:speech.microphone.busy");
      case "OverconstrainedError":
        return i18n.t("core:speech.microphone.overconstrained");
      default:
        break;
    }
  }
  return i18n.t("core:speech.microphone.unavailable");
}

/**
 * `error` event codes from the voice-server (/v1/realtime protocol), with the
 * key of their message (namespace `core`, translated when shown).
 */
const SERVER_ERROR_KEYS = {
  bad_message: "speech.server.badMessage",
  idle_timeout: "speech.server.idleTimeout",
  session_too_long: "speech.server.sessionTooLong",
  upstream_error: "speech.server.upstreamError",
  backpressure: "speech.server.backpressure",
  server_shutdown: "speech.server.shutdown",
  server_error: "speech.server.internal",
} as const;

type ServerErrorCode = keyof typeof SERVER_ERROR_KEYS;

function isServerErrorCode(code: string): code is ServerErrorCode {
  return Object.hasOwn(SERVER_ERROR_KEYS, code);
}

function serverErrorMessage(code: ServerErrorCode): string {
  return i18n.t(SERVER_ERROR_KEYS[code], { ns: "core" });
}

export function describeVoiceServerError(
  code: string | null,
  message: string | null,
): string {
  if (code && isServerErrorCode(code)) return serverErrorMessage(code);
  return message || i18n.t("core:speech.server.generic");
}

/**
 * Voice-server WebSocket close codes. Rejected upgrades (invalid token,
 * non-allowlisted origin, session limit) surface in the browser as an opaque
 * closure (1006): they fall through to the `fallback`.
 */
export function describeVoiceServerClose(
  code: number,
  reason: string | undefined,
  fallback: string,
): string {
  switch (code) {
    case 1001:
      return serverErrorMessage("server_shutdown");
    case 1011:
      return serverErrorMessage("server_error");
    case 4400:
      return serverErrorMessage("bad_message");
    case 4408:
      return serverErrorMessage("idle_timeout");
    case 4413:
      return serverErrorMessage("session_too_long");
    case 4502:
      return serverErrorMessage("upstream_error");
    default:
      return reason || fallback;
  }
}